/**
 * RoomsGuestsPopover — multi-room composition control for hotel search.
 *
 * Sourced from /app/memory/TRIPJACK_HOTEL_BACKEND_FRONTEND_INTEGRATION_GUIDE.md
 * §Rooms + §Children:
 *   - 1-5 rooms (errCode 6017)
 *   - per room: numberOfAdults ≥1 (errCode 6016), numberOfChild 0-12,
 *     childAge[] length must equal numberOfChild (errCode 6007)
 *   - total pax across rooms < 25
 *
 * Emits one consolidated `value` object the parent shapes into the
 * supplier payload:
 *   [{ numberOfAdults: 2, numberOfChild: 1, childAge: [5] }, ...]
 */
import React, { useState, useRef, useEffect } from 'react';
import { Users, Plus, Minus, Bed, BabyIcon } from 'lucide-react';

const MAX_ROOMS = 3;  // Phase H5 — silently capped at 3 (was 5). Add Room button just disables.
const MAX_ADULTS_PER_ROOM = 9;
const MAX_CHILDREN_PER_ROOM = 4;
const MAX_TOTAL_PAX = 24;

const _summary = (rooms) => {
  const totalAdults = rooms.reduce((a, r) => a + r.numberOfAdults, 0);
  const totalChildren = rooms.reduce((a, r) => a + r.numberOfChild, 0);
  return { totalAdults, totalChildren, totalGuests: totalAdults + totalChildren };
};

export const RoomsGuestsPopover = ({ rooms, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const { totalAdults, totalChildren, totalGuests } = _summary(rooms);

  const updateRoom = (idx, patch) => {
    const next = rooms.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };

  const incrementAdults = (idx) => {
    const room = rooms[idx];
    if (room.numberOfAdults >= MAX_ADULTS_PER_ROOM) return;
    if (totalGuests >= MAX_TOTAL_PAX) return;
    updateRoom(idx, { numberOfAdults: room.numberOfAdults + 1 });
  };

  const decrementAdults = (idx) => {
    const room = rooms[idx];
    if (room.numberOfAdults <= 1) return; // ≥1 per Tripjack 6016
    updateRoom(idx, { numberOfAdults: room.numberOfAdults - 1 });
  };

  const incrementChildren = (idx) => {
    const room = rooms[idx];
    if (room.numberOfChild >= MAX_CHILDREN_PER_ROOM) return;
    if (totalGuests >= MAX_TOTAL_PAX) return;
    updateRoom(idx, {
      numberOfChild: room.numberOfChild + 1,
      childAge: [...(room.childAge || []), 5], // default age 5
    });
  };

  const decrementChildren = (idx) => {
    const room = rooms[idx];
    if (room.numberOfChild <= 0) return;
    const nextAges = (room.childAge || []).slice(0, -1);
    updateRoom(idx, {
      numberOfChild: room.numberOfChild - 1,
      childAge: nextAges,
    });
  };

  const setChildAge = (roomIdx, childIdx, age) => {
    const room = rooms[roomIdx];
    const ages = [...(room.childAge || [])];
    ages[childIdx] = Math.max(0, Math.min(12, parseInt(age, 10) || 0));
    updateRoom(roomIdx, { childAge: ages });
  };

  const addRoom = () => {
    if (rooms.length >= MAX_ROOMS) return;
    onChange([...rooms, { numberOfAdults: 1, numberOfChild: 0, childAge: [] }]);
  };

  const removeRoom = (idx) => {
    if (rooms.length <= 1) return;
    onChange(rooms.filter((_, i) => i !== idx));
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="hotel-rooms-trigger"
        className="w-full text-left"
      >
        <p className="text-[9px] font-bold text-[#659A9B] uppercase tracking-wider mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Rooms & Guests
        </p>
        <div className="flex items-start gap-2 mt-0.5">
          <Users className="w-4 h-4 text-[#1B375B] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-bold text-[#1B375B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {rooms.length} Room{rooms.length > 1 ? 's' : ''}, {totalGuests} Guest{totalGuests > 1 ? 's' : ''}
            </p>
            <p className="text-[10px] sm:text-xs text-[#659A9B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {totalAdults} Adult{totalAdults > 1 ? 's' : ''}{totalChildren > 0 ? `, ${totalChildren} Child${totalChildren > 1 ? 'ren' : ''}` : ''}
            </p>
          </div>
        </div>
      </button>

      {open && (
        <div className="absolute top-full mt-2 right-0 z-50 w-[min(92vw,360px)] bg-white rounded-2xl shadow-2xl border border-gray-200 p-4" data-testid="hotel-rooms-popover">
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {rooms.map((room, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-3" data-testid={`hotel-rooms-room-${idx}`}>
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex items-center gap-1.5 text-[#1B375B]">
                    <Bed className="w-3.5 h-3.5" />
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'Manrope, sans-serif' }}>Room {idx + 1}</span>
                  </div>
                  {rooms.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRoom(idx)}
                      className="text-[10px] text-red-500 font-semibold hover:underline"
                      data-testid={`hotel-rooms-remove-${idx}`}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Adults <span className="text-gray-400">(18+)</span></span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => decrementAdults(idx)}
                      disabled={room.numberOfAdults <= 1}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 hover:border-[#1B375B]"
                      data-testid={`hotel-rooms-adults-dec-${idx}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-5 text-center text-sm font-bold text-[#1B375B]" data-testid={`hotel-rooms-adults-count-${idx}`}>{room.numberOfAdults}</span>
                    <button
                      type="button"
                      onClick={() => incrementAdults(idx)}
                      disabled={room.numberOfAdults >= MAX_ADULTS_PER_ROOM || totalGuests >= MAX_TOTAL_PAX}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 hover:border-[#1B375B]"
                      data-testid={`hotel-rooms-adults-inc-${idx}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-700" style={{ fontFamily: 'Manrope, sans-serif' }}>Children <span className="text-gray-400">(0-12)</span></span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => decrementChildren(idx)}
                      disabled={room.numberOfChild <= 0}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 hover:border-[#1B375B]"
                      data-testid={`hotel-rooms-children-dec-${idx}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-5 text-center text-sm font-bold text-[#1B375B]" data-testid={`hotel-rooms-children-count-${idx}`}>{room.numberOfChild}</span>
                    <button
                      type="button"
                      onClick={() => incrementChildren(idx)}
                      disabled={room.numberOfChild >= MAX_CHILDREN_PER_ROOM || totalGuests >= MAX_TOTAL_PAX}
                      className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 hover:border-[#1B375B]"
                      data-testid={`hotel-rooms-children-inc-${idx}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {room.numberOfChild > 0 && (
                  <div className="mt-2.5 pt-2.5 border-t border-gray-200">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#659A9B] mb-1.5" style={{ fontFamily: 'Manrope, sans-serif' }}>Child Ages</p>
                    <div className="grid grid-cols-4 gap-1.5">
                      {(room.childAge || []).map((age, i) => (
                        <select
                          key={i}
                          value={age}
                          onChange={(e) => setChildAge(idx, i, e.target.value)}
                          className="px-1.5 py-1 text-xs rounded-md border border-gray-300 bg-white focus:outline-none focus:border-[#659A9B]"
                          data-testid={`hotel-rooms-childage-${idx}-${i}`}
                        >
                          {Array.from({ length: 13 }, (_, k) => (
                            <option key={k} value={k}>{k} yr</option>
                          ))}
                        </select>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-3">
            <button
              type="button"
              onClick={addRoom}
              disabled={rooms.length >= MAX_ROOMS}
              className="text-xs font-bold text-[#659A9B] hover:underline disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              data-testid="hotel-rooms-add"
            >
              <Plus className="w-3 h-3" /> Add Room
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-1.5 bg-[#1B375B] text-white rounded-lg text-xs font-bold hover:bg-[#659A9B] transition-colors"
              data-testid="hotel-rooms-done"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomsGuestsPopover;
